/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  FormGroup,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  SelectChangeEvent,
  Alert,
  Link,
  Radio,
  RadioGroup,
  Typography,
  CircularProgress,
  Box
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { CartContext } from 'contexts/CartContext'
import { PaymentContext } from 'contexts/PaymentContext'
import { AuthContext } from 'contexts/AuthContext'
import {
  convertToFullDate,
  formatCardNumber,
  formatCvv,
  formatExpirationDate,
  formatZipCode,
  removeSpaces,
  validateCardNumber,
  validateCVV,
  validateExpirationDate,
  validateZipCode
} from 'utils/formatUtils'
import { useNavigate } from 'react-router-dom'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { checkout, checkOrderLockStatus } from 'api/checkout'
import SectionHeader from 'components/SectionHeader'
import CheckoutSummary from 'pages/Checkout/CheckoutSummary'
import CheckoutProcessing from 'pages/Checkout/CheckoutProcessing'
import AddNewCardForm from 'pages/Checkout/AddNewCardForm'
import PaymentMethods from 'components/PaymentMethods'
import { validateWoodenTableRequirements } from 'utils/validateWoodenTable'

type CheckoutFormData = {
  cardNumber: string
  expirationDate: string
  cvv: string
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipCode: string
}

const CheckoutPage = () => {
  const { t } = useTranslation()
  const {
    addresses,
    paymentMethods,
    savePaymentInfo,
    isLoadingPaymentMethods
  } = useContext(PaymentContext)
  const {
    isTermCustomer,
    fetchAccountInfo,
    isLoadingAccountInfo,
    accountInfo
  } = useContext(AuthContext)
  const isPaymentSectionLoading =
    isLoadingAccountInfo || isLoadingPaymentMethods || !accountInfo
  const {
    getCartDetails,
    discountCode,
    setDiscountCode,
    discountResult,
    validateDiscountCode,
    removeDiscount,
    cart,
    clearCartUi,
    isCartReady
  } = useContext(CartContext)

  const {
    cartItemCount,
    subTotal,
    tax,
    assemblyFee,
    totalPrice,
    discountedAmount
  } = getCartDetails()

  const [formValues, setFormValues] = useState<CheckoutFormData>({
    cardNumber: '',
    expirationDate: '',
    cvv: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const { showMessage } = useContext(SnackbarContext)
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    cardNumber: '',
    expirationDate: '',
    cvv: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const [addNewCard, setAddNewCard] = useState<boolean>(false)
  const [useMyAddressChecked, setUseMyAddressChecked] = useState<boolean>(false)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  )
  const navigate = useNavigate()
  const [saveForNextTime, setSaveForNextTime] = useState<boolean>(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [isCheckoutComplete, setIsCheckoutComplete] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [isPollingOrderStatus, setIsPollingOrderStatus] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<
    'CREDIT_CARD' | 'ADD_TO_MY_ACCOUNT'
  >('CREDIT_CARD')

  const woodenTableValidation = validateWoodenTableRequirements(
    cart?.items || []
  )

  // Account information is needed when checkout mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAccountInfo()
  }, [])

  // Polling starts and stops with the order-status flag.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkOngoingOrder = () => {
      const orderInProgress = localStorage.getItem('orderInProgress')
      if (orderInProgress) {
        const orderData = JSON.parse(orderInProgress)
        const orderStartTime = orderData.timestamp
        const now = Date.now()
        const twoMinutes = 2 * 60 * 1000

        if (now - orderStartTime < twoMinutes) {
          setPaymentLoading(true)
          setIsPollingOrderStatus(true)
        } else {
          localStorage.removeItem('orderInProgress')
        }
      }
    }

    checkOngoingOrder()
  }, [])

  // Redirect when a ready cart becomes empty.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let statusCheckTimer: NodeJS.Timeout | null = null
    let checkAttemptCount = 0
    const maxCheckAttempts = 60

    const checkLockStatus = async () => {
      checkAttemptCount++

      const orderData = localStorage.getItem('orderInProgress')
      if (!orderData) {
        setIsPollingOrderStatus(false)
        setPaymentLoading(false)
        return
      }

      try {
        const lockStatus = await checkOrderLockStatus()

        if (!lockStatus.locked) {
          localStorage.removeItem('orderInProgress')
          setIsPollingOrderStatus(false)
          setPaymentLoading(false)

          if (lockStatus.orderCompleted) {
            setIsCheckoutComplete(true)
            clearCartUi()
            navigate('/our-products', { state: { checkoutSuccess: true } })
          } else {
            setCheckoutError(lockStatus.message || 'Order processing failed.')
          }
        }
      } catch (error) {
        console.error('Error checking lock status:', error)
      }

      if (checkAttemptCount >= maxCheckAttempts) {
        localStorage.removeItem('orderInProgress')
        setIsPollingOrderStatus(false)
        setPaymentLoading(false)
        setCheckoutError('Order processing is taking longer than expected.')
      }
    }

    if (isPollingOrderStatus) {
      checkLockStatus()
      statusCheckTimer = setInterval(checkLockStatus, 2000)
    }

    return () => {
      if (statusCheckTimer) {
        clearInterval(statusCheckTimer)
      }
    }
  }, [isPollingOrderStatus, navigate])

  useEffect(() => {
    if (!isTermCustomer && paymentMethod === 'ADD_TO_MY_ACCOUNT') {
      setPaymentMethod('CREDIT_CARD')
    }
  }, [isTermCustomer, paymentMethod])

  useEffect(() => {
    if (
      !isCheckoutComplete &&
      !isPollingOrderStatus &&
      !paymentLoading &&
      isCartReady &&
      cartItemCount === 0
    ) {
      showMessage(t('snackbar.cart.emptyCart'), 'error')
      navigate('/our-products')
    }
  }, [
    isCheckoutComplete,
    isPollingOrderStatus,
    paymentLoading,
    isCartReady,
    cartItemCount
  ])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target

    let formattedValue = value

    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value)
    } else if (name === 'expirationDate') {
      formattedValue = formatExpirationDate(value)
    } else if (name === 'cvv') {
      formattedValue = formatCvv(value)
    } else if (name === 'zipCode') {
      formattedValue = formatZipCode(value)
    } else if (name in formValues) {
      formattedValue = value
    }

    setFormValues({
      ...formValues,
      [name]: formattedValue
    })
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let errorMessage = ''

    if (name === 'cardNumber') {
      errorMessage = validateCardNumber(value)
    } else if (name === 'expirationDate') {
      errorMessage = validateExpirationDate(value)
        ? ''
        : 'Expiration date must be MM/YY format.'
    } else if (name === 'cvv') {
      errorMessage = validateCVV(value) ? '' : 'CVV must be exactly 3 digits.'
    } else if (name === 'zipCode') {
      errorMessage = validateZipCode(value)
        ? ''
        : 'Zip Code must be in 5-digit or ZIP+4 format (e.g., 12345 or 12345-6789).'
    } else if (
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'addressLine1' ||
      name === 'city' ||
      name === 'state'
    ) {
      errorMessage =
        value?.trim() === '' || !value ? 'This field is required.' : ''
    }

    setErrorMessages(prev => ({
      ...prev,
      [name]: errorMessage
    }))
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target
    if (name) {
      setFormValues({ ...formValues, [name]: value })
      setErrorMessages(prev => ({
        ...prev,
        [name]: value?.trim() === '' || !value ? 'This field is required.' : ''
      }))
    }
  }

  const handleUseMyAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    setUseMyAddressChecked(isChecked)

    if (!isChecked) {
      setFormValues(prevValues => ({
        ...prevValues,
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: ''
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate WOODEN TABLE requirements
    if (!woodenTableValidation.isValid) {
      setCheckoutError(
        woodenTableValidation.warning || 'Please check your cart items.'
      )
      return
    }

    setPaymentLoading(true)

    // Set order in progress flag in localStorage
    localStorage.setItem(
      'orderInProgress',
      JSON.stringify({
        timestamp: Date.now(),
        poNumber: cart.poNumber
      })
    )

    if (Number(totalPrice) === 0) {
      const result = await checkout({
        poNumber: cart.poNumber,
        discountCode: discountCode || undefined
      })

      if (result.success) {
        localStorage.removeItem('orderInProgress')
        setIsCheckoutComplete(true)
        clearCartUi()
        navigate('/our-products', { state: { checkoutSuccess: true } })
        return
      } else {
        localStorage.removeItem('orderInProgress')
        setPaymentLoading(false)
        const isCartEmptyError = result.error?.includes('Your cart is empty')

        if (isCartEmptyError) {
          clearCartUi()
          showMessage(
            'Your cart is empty. Please add items before checking out.',
            'error'
          )
          navigate('/our-products')
        } else {
          setCheckoutError(result.error || 'An unexpected error occurred.')
        }
      }
      return
    }

    if (Number(totalPrice) > 0 && paymentMethod === 'ADD_TO_MY_ACCOUNT') {
      const result = await checkout({
        poNumber: cart.poNumber,
        discountCode: discountCode || undefined
      })

      if (result.success) {
        localStorage.removeItem('orderInProgress')
        setIsCheckoutComplete(true)
        clearCartUi()
        showMessage(t('checkout.addToMyAccountHelp'), 'success', 6000)
        navigate('/our-products', { state: { checkoutSuccess: true } })
        return
      }
      localStorage.removeItem('orderInProgress')
      setPaymentLoading(false)
      const isCartEmptyError = result.error?.includes('Your cart is empty')
      if (isCartEmptyError) {
        clearCartUi()
        showMessage(t('snackbar.cart.emptyCart'), 'error')
        navigate('/our-products')
      } else {
        setCheckoutError(result.error || 'An unexpected error occurred.')
      }
      return
    }

    const newErrors: Record<string, string> = {}

    if (addNewCard) {
      newErrors.cardNumber = validateCardNumber(formValues.cardNumber)

      newErrors.expirationDate = validateExpirationDate(
        formValues.expirationDate
      )
        ? ''
        : 'Expiration date must be MM/YY format.'

      newErrors.cvv = validateCVV(formValues.cvv)
        ? ''
        : 'CVV must be exactly 3 digits.'

      if (!useMyAddressChecked || !selectedAddressId) {
        newErrors.firstName = formValues.firstName?.trim()
          ? ''
          : 'First name is required.'

        newErrors.lastName = formValues.lastName?.trim()
          ? ''
          : 'Last name is required.'

        newErrors.addressLine1 = formValues.addressLine1?.trim()
          ? ''
          : 'Address Line 1 is required.'

        newErrors.city = formValues.city?.trim() ? '' : 'City is required.'

        newErrors.state = formValues.state?.trim() ? '' : 'State is required.'

        newErrors.zipCode = validateZipCode(formValues.zipCode)
          ? ''
          : 'Zip Code must be in 5-digit or ZIP+4 format (e.g., 12345 or 12345-6789).'
      }
    }

    setErrorMessages(newErrors)

    const hasErrors = Object.values(newErrors).some(error => error)

    if (!hasErrors) {
      if (selectedPaymentMethodId) {
        const selectedPaymentMethod = paymentMethods.find(
          method => method.paymentProfileId === selectedPaymentMethodId
        )

        if (!selectedPaymentMethod) {
          localStorage.removeItem('orderInProgress')
          showMessage(t('snackbar.payment.methodNotFound'), 'error')
          setPaymentLoading(false)
          return
        }

        const result = await checkout({
          paymentProfileId: selectedPaymentMethod.paymentProfileId,
          poNumber: cart.poNumber,
          discountCode: discountCode || undefined,
          firstName: selectedPaymentMethod.billingAddress.firstName,
          lastName: selectedPaymentMethod.billingAddress.lastName,
          address: selectedPaymentMethod.billingAddress.address,
          city: selectedPaymentMethod.billingAddress.city,
          state: selectedPaymentMethod.billingAddress.state,
          zipCode: selectedPaymentMethod.billingAddress.zipCode
        })

        if (result.success) {
          localStorage.removeItem('orderInProgress')
          setIsCheckoutComplete(true)
          clearCartUi()
          navigate('/our-products', { state: { checkoutSuccess: true } })
          return
        } else {
          localStorage.removeItem('orderInProgress')
          setPaymentLoading(false)
          const isCartEmptyError = result.error?.includes('Your cart is empty')

          if (isCartEmptyError) {
            clearCartUi()
            showMessage(t('snackbar.cart.emptyCart'), 'error')
            navigate('/our-products')
          } else {
            setCheckoutError(result.error || 'An unexpected error occurred.')
          }
        }
      } else if (addNewCard) {
        const result = await checkout({
          cardNumber: removeSpaces(formValues.cardNumber),
          expirationDate: convertToFullDate(formValues.expirationDate),
          cvv: formValues.cvv,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          address:
            `${formValues.addressLine1} ${formValues.addressLine2}`.trim(),
          city: formValues.city,
          state: formValues.state,
          zipCode: formValues.zipCode,
          poNumber: cart.poNumber,
          discountCode: discountCode || undefined
        })

        if (result.success) {
          if (saveForNextTime) {
            await savePaymentInfo({
              cardNumber: removeSpaces(formValues.cardNumber),
              expirationDate: formValues.expirationDate,
              cvv: formValues.cvv,
              billingAddress: {
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                address:
                  `${formValues.addressLine1} ${formValues.addressLine2}`.trim(),
                city: formValues.city,
                state: formValues.state,
                zipCode: formValues.zipCode
              }
            })
          }
          localStorage.removeItem('orderInProgress')
          setIsCheckoutComplete(true)
          clearCartUi()
          navigate('/our-products', { state: { checkoutSuccess: true } })
          return
        } else {
          localStorage.removeItem('orderInProgress')
          setPaymentLoading(false)
          const isCartEmptyError = result.error?.includes('Your cart is empty')
          if (isCartEmptyError) {
            clearCartUi()
            showMessage(t('snackbar.cart.emptyCart'), 'error')
            navigate('/our-products')
          } else {
            setCheckoutError(result.error || 'An unexpected error occurred.')
          }
        }
      }
    } else {
      localStorage.removeItem('orderInProgress')
      showMessage(t('snackbar.checkout.formErrors'), 'error')
      setPaymentLoading(false)
    }
  }

  const handlePaymentMethodClick = (paymentProfileId: string) => {
    if (selectedPaymentMethodId === paymentProfileId) {
      setSelectedPaymentMethodId(null)
    } else {
      setSelectedPaymentMethodId(paymentProfileId)
      setAddNewCard(false)
    }
  }

  const handleAddNewCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddNewCard(e.target.checked)
    setSelectedPaymentMethodId(null)
  }

  const handleAddressSelect = (addressId: string) => {
    if (selectedAddressId === addressId) {
      setSelectedAddressId(null)
    } else {
      const selectedAddress = addresses.find(
        address => address.id === addressId
      )
      if (selectedAddress) {
        const [firstName, lastName] = selectedAddress.customerName.split(' ')

        setFormValues({
          ...formValues,
          firstName: firstName || '',
          lastName: lastName || '',
          addressLine1: selectedAddress.addressLine1,
          addressLine2: selectedAddress.addressLine2 || '',
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode
        })

        setSelectedAddressId(addressId)
      }
    }
  }

  return (
    <FixedWidthContainer
      sx={{
        position: 'relative',
        minHeight: '100vh'
      }}
    >
      {paymentLoading ? (
        <CheckoutProcessing itemCount={cartItemCount} />
      ) : (
        <>
          <SectionHeader title={t('checkout.title')} />
          <Grid container spacing={3}>
            <Grid item xs={8} container>
              <form
                onSubmit={handleSubmit}
                style={{
                  width: '100%'
                }}
              >
                {Number(totalPrice) > 0 && isPaymentSectionLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 6
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {Number(totalPrice) > 0 && (
                      <>
                        <Typography variant='h3'>
                          {t('checkout.paymentMethod')}
                        </Typography>

                        {isTermCustomer && (
                          <FormControl
                            component='fieldset'
                            sx={{ mb: 2, width: '100%' }}
                          >
                            <RadioGroup
                              row
                              value={paymentMethod}
                              onChange={e =>
                                setPaymentMethod(
                                  e.target.value as
                                    | 'CREDIT_CARD'
                                    | 'ADD_TO_MY_ACCOUNT'
                                )
                              }
                            >
                              <FormControlLabel
                                value='CREDIT_CARD'
                                control={
                                  <Radio sx={{ color: 'primary.main' }} />
                                }
                                label={t('checkout.payByCard')}
                              />
                              <FormControlLabel
                                value='ADD_TO_MY_ACCOUNT'
                                control={
                                  <Radio sx={{ color: 'primary.main' }} />
                                }
                                label={t('checkout.addToMyAccount')}
                              />
                            </RadioGroup>
                          </FormControl>
                        )}

                        {paymentMethod === 'ADD_TO_MY_ACCOUNT' && (
                          <Alert
                            severity='info'
                            sx={{ mb: 2, borderRadius: 2 }}
                          >
                            {t('checkout.addToMyAccountHelp')}
                          </Alert>
                        )}

                        {paymentMethod === 'CREDIT_CARD' && (
                          <>
                            <PaymentMethods
                              paymentMethods={paymentMethods}
                              selectedPaymentMethodId={selectedPaymentMethodId}
                              handlePaymentMethodClick={
                                handlePaymentMethodClick
                              }
                            />

                            <FormGroup sx={{ color: '#8F8F8F' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    sx={{
                                      '&.Mui-checked': {
                                        color: 'primary.main'
                                      }
                                    }}
                                    checked={addNewCard}
                                    onChange={handleAddNewCardChange}
                                  />
                                }
                                label={t('checkout.addNew')}
                              />
                            </FormGroup>

                            {!selectedPaymentMethodId && addNewCard && (
                              <AddNewCardForm
                                formValues={formValues}
                                errors={errorMessages}
                                handleInputChange={handleInputChange}
                                handleBlur={handleBlur}
                                handleSelectChange={handleSelectChange}
                                useMyAddressChecked={useMyAddressChecked}
                                handleUseMyAddressChange={
                                  handleUseMyAddressChange
                                }
                                saveForNextTime={saveForNextTime}
                                handleSaveForNextTimeChange={e =>
                                  setSaveForNextTime(e.target.checked)
                                }
                                addresses={addresses}
                                selectedAddressId={selectedAddressId}
                                handleAddressSelect={handleAddressSelect}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}

                    {woodenTableValidation.warning && (
                      <Alert severity='error' sx={{ mt: 2, borderRadius: 2 }}>
                        {woodenTableValidation.messageParts ? (
                          <>
                            {woodenTableValidation.messageParts.beforeLink}
                            <Link
                              href='/search?keyword=-ber'
                              sx={{
                                color: 'error.main',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                            >
                              {woodenTableValidation.messageParts.linkText}
                            </Link>
                            {woodenTableValidation.messageParts.afterLink}
                          </>
                        ) : (
                          woodenTableValidation.warning
                        )}
                      </Alert>
                    )}

                    {checkoutError && (
                      <Alert
                        severity='error'
                        onClose={() => setCheckoutError(null)}
                        sx={{ mt: 2, borderRadius: 2 }}
                      >
                        {checkoutError}
                      </Alert>
                    )}

                    <Button
                      sx={{
                        width: '100%',
                        borderRadius: '10px',
                        marginTop: 2,
                        marginBottom: 8
                      }}
                      type='submit'
                      variant='contained'
                      disabled={
                        !woodenTableValidation.isValid ||
                        (Number(totalPrice) > 0 &&
                          paymentMethod === 'CREDIT_CARD' &&
                          !selectedPaymentMethodId &&
                          !addNewCard)
                      }
                    >
                      {t('checkout.placeOrder')}
                    </Button>
                  </>
                )}
              </form>
            </Grid>

            <Grid item xs={4} container justifyContent='flex-end'>
              <CheckoutSummary
                cart={cart}
                subTotal={subTotal}
                discountedAmount={discountedAmount}
                discountResult={discountResult}
                tax={tax}
                assemblyFee={assemblyFee}
                totalPrice={totalPrice}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                validateDiscountCode={validateDiscountCode}
                removeDiscount={removeDiscount}
              />
            </Grid>
          </Grid>
        </>
      )}
    </FixedWidthContainer>
  )
}

export default CheckoutPage
